
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, FileText, Flame, Video, Clock, Image as ImageIcon, Trash2, Eye, EyeOff } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ContentItem, PaginationState } from "./types";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious
} from "@/components/ui/pagination";

interface ContentTableProps {
  content: ContentItem[];
  onChangeCredits: (contentId: number, newCredits: number) => void;
  onToggleVisibility: (contentId: number) => void;
  onTogglePremiere: (contentId: number) => void;
  onDeleteContent: (contentId: number) => void;
  pagination: PaginationState;
  onPageChange: (page: number) => void;
}

export function ContentTable({ 
  content,
  onChangeCredits,
  onToggleVisibility,
  onTogglePremiere,
  onDeleteContent,
  pagination,
  onPageChange
}: ContentTableProps) {
  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="h-4 w-4" />;
      case 'document':
        return <FileText className="h-4 w-4" />;
      case 'course':
        return <BookOpen className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(pagination.totalItems / pagination.pageSize));
  
  // Generate page numbers array for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total pages is less than or equal to maxPagesToShow
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always include first page
      pages.push(1);
      
      // Current page neighborhood
      const startPage = Math.max(2, pagination.currentPage - 1);
      const endPage = Math.min(pagination.currentPage + 1, totalPages - 1);
      
      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push(-1); // -1 represents ellipsis
      }
      
      // Add pages around current page
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push(-2); // -2 represents ellipsis (using different value to ensure key uniqueness)
      }
      
      // Always include last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b">
              <th className="py-2 px-4 text-left">ID</th>
              <th className="py-2 px-4 text-left">Τίτλος</th>
              <th className="py-2 px-4 text-left">Τύπος</th>
              <th className="py-2 px-4 text-left">Διάρκεια</th>
              <th className="py-2 px-4 text-left">Προεπισκόπηση</th>
              <th className="py-2 px-4 text-left">Credits</th>
              <th className="py-2 px-4 text-left">Κατάσταση</th>
              <th className="py-2 px-4 text-left">Πρεμιέρα</th>
              <th className="py-2 px-4 text-left">Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {content.map(item => (
              <tr key={item.id} className="border-b">
                <td className="py-2 px-4">{item.id}</td>
                <td className="py-2 px-4">{item.title}</td>
                <td className="py-2 px-4 flex items-center">
                  {getContentTypeIcon(item.type)}
                  <span className="ml-2">{item.type}</span>
                </td>
                <td className="py-2 px-4">
                  {(item.type === 'video' || item.type === 'course') && (
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{item.duration || 'N/A'}</span>
                    </div>
                  )}
                </td>
                <td className="py-2 px-4">
                  {item.type === 'video' && item.thumbnailUrl && (
                    <div className="w-16 h-9 relative overflow-hidden rounded border">
                      <AspectRatio ratio={16/9}>
                        <img 
                          src={item.thumbnailUrl} 
                          alt={`${item.title} thumbnail`}
                          className="w-full h-full object-cover"
                        />
                      </AspectRatio>
                    </div>
                  )}
                </td>
                <td className="py-2 px-4">
                  <Input 
                    type="number" 
                    min="1" 
                    value={item.credits} 
                    onChange={(e) => onChangeCredits(item.id, parseInt(e.target.value) || 1)}
                    className="w-16 h-8 text-sm"
                  />
                </td>
                <td className="py-2 px-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${item.isPublic ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {item.isPublic ? 'Δημόσιο' : 'Ιδιωτικό'}
                  </span>
                </td>
                <td className="py-2 px-4">
                  {item.isPremiere && (
                    <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800 flex items-center w-fit">
                      <Flame className="h-3 w-3 mr-1" /> Πρεμιέρα
                    </span>
                  )}
                </td>
                <td className="py-2 px-4">
                  <div className="flex space-x-2">
                    <Button 
                      variant={item.isPublic ? "destructive" : "default"}
                      size="sm"
                      onClick={() => onToggleVisibility(item.id)}
                    >
                      {item.isPublic ? (
                        <><EyeOff className="h-4 w-4 mr-1" /> Ιδιωτικό</>
                      ) : (
                        <><Eye className="h-4 w-4 mr-1" /> Δημόσιο</>
                      )}
                    </Button>
                    <Button 
                      variant={item.isPremiere ? "default" : "outline"}
                      size="sm"
                      onClick={() => onTogglePremiere(item.id)}
                    >
                      {item.isPremiere ? 'Αφαίρεση Πρεμιέρας' : 'Ορισμός ως Πρεμιέρα'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDeleteContent(item.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Διαγραφή
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            {pagination.currentPage > 1 && (
              <PaginationItem>
                <PaginationPrevious 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(pagination.currentPage - 1);
                  }} 
                />
              </PaginationItem>
            )}
            
            {getPageNumbers().map((pageNum, index) => (
              <PaginationItem key={`page-${pageNum}-${index}`}>
                {pageNum < 0 ? (
                  <span className="flex h-9 w-9 items-center justify-center">...</span>
                ) : (
                  <PaginationLink 
                    href="#" 
                    isActive={pageNum === pagination.currentPage}
                    onClick={(e) => {
                      e.preventDefault();
                      onPageChange(pageNum);
                    }}
                  >
                    {pageNum}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}
            
            {pagination.currentPage < totalPages && (
              <PaginationItem>
                <PaginationNext 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(pagination.currentPage + 1);
                  }} 
                />
              </PaginationItem>
            )}
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
